import React from 'react';
import { styled, keyframes, alpha } from '@mui/material/styles';

/** Tab favicon — rounded white tile with the millet m. */
export const MILLET_FAVICON_SRC = `${process.env.PUBLIC_URL || ''}/favicon.svg`;

/** Preset font sizes (px) for common logo placements */
export const MILLET_WORDMARK_SIZES = {
  xs: 12,
  sm: 16,
  header: 18,
  md: 20,
  auth: 22,
  lg: 26,
  xl: 34,
};

const LIVE_PULSE_DURATION_S = 1.4;

const liveDotPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 transparent;
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 7px 2px var(--millet-dot-glow);
  }
`;

const Root = styled('span', {
  shouldForwardProp: (prop) => !['$fontSize', '$variant'].includes(prop),
})(({ theme, $fontSize, $variant }) => {
  const dotSize = Math.round($fontSize * 0.22);
  return {
    position: 'relative',
    display: 'inline-block',
    lineHeight: 1,
    paddingRight: dotSize + Math.round($fontSize * 0.04),
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    letterSpacing: $variant === 'mark' ? '-0.02em' : '-0.035em',
    color: theme.palette.text.primary,
    fontSize: $fontSize,
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };
});

const Dot = styled('span', {
  shouldForwardProp: (prop) => prop !== '$fontSize',
})(({ theme, $fontSize }) => {
  const dotSize = Math.round($fontSize * 0.22);
  const accent = theme.palette.accent?.main || theme.palette.favorite?.main || '#F57EB3';
  return {
    position: 'absolute',
    top: 0,
    right: 0,
    width: dotSize,
    height: dotSize,
    borderRadius: '50%',
    backgroundColor: accent,
    transformOrigin: 'center',
    '--millet-dot-glow': alpha(accent, 0.32),
    animation: `${liveDotPulse} ${LIVE_PULSE_DURATION_S}s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  };
});

/**
 * millet wordmark — "millet" or "m" with a blush pink dot.
 * @param {'wordmark' | 'mark'} variant - Full wordmark or compact m mark
 * @param {keyof typeof MILLET_WORDMARK_SIZES | number} size - Preset or explicit px font size
 */
function MilletWordmark({
  variant = 'wordmark',
  size = 'header',
  fontSize: fontSizeProp,
  className,
  'aria-hidden': ariaHidden,
}) {
  const fontSize = fontSizeProp
    ?? (typeof size === 'number' ? size : MILLET_WORDMARK_SIZES[size] ?? MILLET_WORDMARK_SIZES.header);

  return (
    <Root
      className={className}
      $fontSize={fontSize}
      $variant={variant}
      aria-hidden={ariaHidden}
      aria-label={ariaHidden ? undefined : 'millet'}
    >
      {variant === 'mark' ? 'm' : 'millet'}
      <Dot $fontSize={fontSize} aria-hidden />
    </Root>
  );
}

export default MilletWordmark;
